import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  GridColDef,
  GridRenderCellParams,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import StyledDataGrid from 'shared/components/StyledDataGrid';
import CreativePerformanceService, {
  MAX_ROWS,
} from 'services/CreativePerformanceService';
import CreativeInsightsService from 'services/CreativeInsightsService';
import CreativeThumbnail from '../components/CreativeThumbnail';
import TagEditorDialog from '../tags/TagEditorDialog';
import { TaggedCreative, TagField, VocabValue } from '../tags/types';
import { CreativeRow, MediaFilter } from '../types';
import { buildMetricCol } from '../metricCol';
import {
  DATE_PRESETS,
  creativeThumbnailSrc,
  fmtInt,
  fmtMoney,
  fmtMoneyExact,
  fmtPct,
  fmtRatio,
  isoDaysAgo,
  isoToday,
  profitTone,
  roasTone,
  toNum,
} from '../utils';
import {
  buildInsightRows,
  Dimension,
  DIMENSION,
  DIMENSION_LABELS,
  InsightRow,
  NOT_TAGGED,
} from './aggregate';

const DEFAULT_DAYS = 90;
const GROUP_ROW_HEIGHT = 52;
const CREATIVE_ROW_HEIGHT = 60;
const MEDIA_OPTIONS: MediaFilter[] = ['all', 'video', 'static', 'carousel'];

// The first column: a group row is the tag value with an expand toggle; a
// creative row is its thumbnail and name, indented under the group.
function GroupingCell(
  props: GridRenderCellParams & { onOpenCreative: (key: string) => void },
) {
  const apiRef = useGridApiContext();
  const row = props.row as InsightRow;
  const node = props.rowNode as { type: string; childrenExpanded?: boolean };

  if (row.kind === 'group') {
    const expanded = Boolean(node.childrenExpanded);
    const muted = row.groupKey === NOT_TAGGED || row.groupKey.startsWith('__');
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          width: '100%',
        }}
      >
        <IconButton
          size="small"
          aria-label={expanded ? 'Hide creatives' : 'Show creatives'}
          onClick={event => {
            event.stopPropagation();
            apiRef.current.setRowChildrenExpansion(props.id, !expanded);
          }}
        >
          {expanded ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        <Tooltip title={row.label} placement="top-start">
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: 600,
              color: muted ? '#64748B' : '#0F172A',
              minWidth: 0,
            }}
          >
            {row.label}
          </Typography>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{ color: '#64748B', flexShrink: 0, ml: 0.5 }}
        >
          {row.creative_count} creative{row.creative_count === 1 ? '' : 's'}
        </Typography>
      </Box>
    );
  }

  const creative = row.creative as CreativeRow;
  const src = creativeThumbnailSrc(
    creative.thumb_proxy_url,
    creative.thumbnail_url,
    creative.image_url,
  );
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        pl: 4.5,
        minWidth: 0,
        width: '100%',
        cursor: 'pointer',
      }}
      onClick={() => props.onOpenCreative(creative.group_key)}
    >
      <CreativeThumbnail src={src} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap title={row.label}>
          {row.label}
        </Typography>
        <Typography
          variant="caption"
          noWrap
          sx={{ color: '#64748B', display: 'block' }}
        >
          {creative.media_type} · {creative.ad_count} ad
          {Number(creative.ad_count) === 1 ? '' : 's'}
          {creative.campaign_name ? ` · ${creative.campaign_name}` : ''}
        </Typography>
      </Box>
    </Box>
  );
}

// Performance grouped by tag. Pick a dimension (persona, lead selling point,
// hook…); every row is one value with the combined numbers of the creatives
// behind it, and expands to list those creatives with their own numbers.
export default function InsightsTab() {
  const performance = useMemo(() => new CreativePerformanceService(), []);
  const insights = useMemo(() => new CreativeInsightsService(), []);

  const [start, setStart] = useState(isoDaysAgo(DEFAULT_DAYS - 1));
  const [end, setEnd] = useState(isoToday());
  const [dimension, setDimension] = useState<Dimension>(DIMENSION.PERSONA);
  const [media, setMedia] = useState<MediaFilter>('all');

  const [creatives, setCreatives] = useState<CreativeRow[]>([]);
  const [multiplier, setMultiplier] = useState<number | null>(null);
  const [tagged, setTagged] = useState<TaggedCreative[]>([]);
  const [vocabulary, setVocabulary] = useState<VocabValue[]>([]);
  const [loading, setLoading] = useState(true);
  // Separate, so a performance reload that succeeds can't hide a tags failure
  // (which would silently put every creative under "Not tagged yet").
  const [tagsError, setTagsError] = useState<string | null>(null);
  const [perfError, setPerfError] = useState<string | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const fetchId = useRef(0);

  // Tags don't depend on the date window, so they load once (and again after a
  // correction); performance reloads whenever the window changes.
  const loadTags = useCallback(async () => {
    const [t, v] = await Promise.all([
      insights.getCreatives(),
      insights.getVocabulary(),
    ]);
    if (!t || !v) {
      setTagsError('Could not load creative tags.');
      return;
    }
    setTagsError(null);
    setTagged(t.rows);
    setVocabulary(v.values);
  }, [insights]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  useEffect(() => {
    const id = ++fetchId.current;
    setLoading(true);
    performance
      .getAds({
        start,
        end,
        groupBy: 'creative',
        level: 'ad',
        assetType: 'headline',
      })
      .then(res => {
        if (id !== fetchId.current) return;
        if (!res) {
          setPerfError('Could not load creative performance for this window.');
          // Don't leave the previous window's rows under the new dates.
          setCreatives([]);
        } else {
          setPerfError(null);
          setCreatives(res.rows);
          setMultiplier(res.meta.trueRoasMultiplier);
        }
        setLoading(false);
      });
  }, [performance, start, end]);

  const tagsByKey = useMemo(
    () => new Map(tagged.map(t => [t.creative_key, t])),
    [tagged],
  );
  const inScope = useMemo(
    () =>
      creatives.filter(
        c =>
          (toNum(c.spend) || 0) > 0 &&
          (media === 'all' || c.media_type === media),
      ),
    [creatives, media],
  );
  const { rows, totalSpend } = useMemo(
    () =>
      buildInsightRows(dimension, inScope, tagsByKey, vocabulary, multiplier),
    [dimension, inScope, tagsByKey, vocabulary, multiplier],
  );
  const taggedSpend = useMemo(
    () =>
      inScope.reduce(
        (s, c) =>
          tagsByKey.get(c.group_key)?.status === 'done'
            ? s + (toNum(c.spend) || 0)
            : s,
        0,
      ),
    [inScope, tagsByKey],
  );

  const openCreative = tagged.find(t => t.creative_key === openKey) || null;
  const setTag = async (
    creativeKey: string,
    field: TagField,
    value: string | string[] | null,
  ) => {
    await insights.setTag(creativeKey, field, value);
    await loadTags();
  };

  const metric = (
    field: keyof InsightRow & string,
    name: string,
    format: (v: unknown) => string,
    opts = {},
  ) => buildMetricCol<InsightRow>(field, name, format, opts);

  const columns: GridColDef[] = [
    metric('spend_share', 'Share of spend', v => fmtPct(v, 1), {
      width: 110,
      help: 'This row’s spend as a share of all spend in the window (after the media filter).',
    }),
    metric('spend', 'Spend', fmtMoney),
    metric('nc_roas', 'NC ROAS', fmtRatio, {
      width: 100,
      tone: (r: InsightRow) => roasTone(r.nc_roas),
      help: 'New-customer revenue ÷ spend (Triple Whale). The closest read on finding new customers.',
    }),
    metric('nc_cpa', 'NC CPA', fmtMoney, {
      width: 100,
      help: 'Spend ÷ new-customer orders',
    }),
    metric('true_roas', 'True ROAS', fmtRatio, {
      width: 104,
      tone: (r: InsightRow) => roasTone(r.true_roas),
      help: `Meta-reported purchase value × ${multiplier ?? 'the incrementality multiplier'} ÷ spend, summed across the group.`,
    }),
    metric('roas', 'ROAS', fmtRatio, {
      width: 90,
      tone: (r: InsightRow) => roasTone(r.roas),
      help: 'Triple Whale revenue ÷ spend',
    }),
    metric('revenue', 'Revenue', fmtMoney),
    metric('profit', 'Profit', fmtMoney, {
      tone: (r: InsightRow) => profitTone(r.profit),
    }),
    metric('true_profit', 'True profit', fmtMoney, {
      width: 116,
      tone: (r: InsightRow) => profitTone(r.true_profit),
    }),
    metric('poas', 'POAS', fmtRatio, {
      width: 90,
      tone: (r: InsightRow) => profitTone(r.poas),
    }),
    metric('orders', 'Orders', fmtInt, { width: 92 }),
    metric('nc_orders', 'NC orders', fmtInt, { width: 104 }),
    metric('cpa', 'CPA', fmtMoney, { width: 90 }),
    metric('aov', 'AOV', fmtMoney, { width: 90 }),
    metric('meta_roas', 'Meta ROAS', fmtRatio, { width: 108 }),
    metric('ctr', 'CTR', v => fmtPct(v, 2), { width: 90 }),
    metric('cpm', 'CPM', fmtMoney, { width: 90 }),
    metric('cvr', 'CVR', v => fmtPct(v, 2), {
      width: 90,
      help: 'Orders ÷ sessions',
    }),
    metric('atc_rate', 'ATC rate', v => fmtPct(v, 1), { width: 100 }),
    metric('impressions', 'Impr.', fmtInt, { width: 104 }),
    metric('hold_rate', '25% watched', v => fmtPct(v, 1), {
      width: 112,
      help: 'Video ads only: 25%-watched ÷ video impressions',
    }),
    metric('thruplay_rate', 'ThruPlay', v => fmtPct(v, 1), {
      width: 100,
      help: 'Video ads only: ThruPlays ÷ video impressions',
    }),
    metric('ad_count', 'Ads', fmtInt, { width: 80 }),
  ];

  const groupingColDef: Partial<GridColDef> = {
    headerName: DIMENSION_LABELS[dimension],
    width: 380,
    sortable: false,
    renderCell: params => (
      <GroupingCell {...params} onOpenCreative={setOpenKey} />
    ),
  };

  const applyPreset = (days: number) => {
    setStart(isoDaysAgo(days - 1));
    setEnd(isoToday());
  };
  const activePreset = DATE_PRESETS.find(
    p => start === isoDaysAgo(p.days - 1) && end === isoToday(),
  );
  const groupCount = rows.filter(r => r.kind === 'group').length;

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ color: '#64748B', mb: 2, maxWidth: 900 }}
      >
        Performance grouped by tag. Each row adds up every creative carrying
        that tag in the window; open a row to see the creatives behind it. Rates
        are recomputed from the group’s totals, so a group is never an average
        of averages.
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          mb: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          select
          size="small"
          label="Group by"
          value={dimension}
          onChange={e => setDimension(e.target.value as Dimension)}
          sx={{ minWidth: 220 }}
        >
          {(Object.keys(DIMENSION_LABELS) as Dimension[]).map(d => (
            <MenuItem key={d} value={d}>
              {DIMENSION_LABELS[d]}
            </MenuItem>
          ))}
        </TextField>
        <ButtonGroup size="small">
          {DATE_PRESETS.map(p => (
            <Button
              key={p.label}
              variant={activePreset?.days === p.days ? 'contained' : 'outlined'}
              onClick={() => applyPreset(p.days)}
            >
              {p.label}
            </Button>
          ))}
        </ButtonGroup>
        <TextField
          size="small"
          type="date"
          value={start}
          disabled={loading}
          onChange={e => e.target.value && setStart(e.target.value)}
          inputProps={{ 'aria-label': 'Start date' }}
        />
        <Typography variant="body2" sx={{ color: '#64748B' }}>
          to
        </Typography>
        <TextField
          size="small"
          type="date"
          value={end}
          disabled={loading}
          onChange={e => e.target.value && setEnd(e.target.value)}
          inputProps={{ 'aria-label': 'End date' }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={media}
          onChange={(_, v) => v && setMedia(v)}
        >
          {MEDIA_OPTIONS.map(m => (
            <ToggleButton
              key={m}
              value={m}
              sx={{ textTransform: 'none', px: 1.5 }}
            >
              {m === 'all' ? 'All' : m.charAt(0).toUpperCase() + m.slice(1)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Paper>

      {tagsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTagsError(null)}>
          {tagsError}
        </Alert>
      )}
      {perfError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPerfError(null)}>
          {perfError}
        </Alert>
      )}
      {!loading && creatives.length >= MAX_ROWS && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This window has more than {fmtInt(MAX_ROWS)} creatives, so only the{' '}
          {fmtInt(MAX_ROWS)} highest-spending are counted and the totals are
          short. Pick a shorter window.
        </Alert>
      )}
      {!loading && totalSpend > 0 && taggedSpend / totalSpend < 0.9 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Only {fmtPct(taggedSpend / totalSpend, 0)} of this window’s spend is
          on tagged creatives. The rest sits in “Not tagged yet” until tagging
          catches up (Tags tab).
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: 'flex',
            gap: 3,
            flexWrap: 'wrap',
            borderBottom: '1px solid #E2E8F0',
          }}
        >
          <Typography variant="body2">
            <strong>{groupCount}</strong>{' '}
            {DIMENSION_LABELS[dimension].toLowerCase()} values
          </Typography>
          <Typography variant="body2">
            <strong>{inScope.length.toLocaleString()}</strong> creatives with
            spend
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            <strong>{fmtMoneyExact(totalSpend)}</strong> spend
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            <strong>
              {fmtPct(totalSpend ? taggedSpend / totalSpend : null, 0)}
            </strong>{' '}
            of spend tagged
          </Typography>
        </Box>
        <Box sx={{ height: 680, position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
                backgroundColor: 'rgba(255,255,255,0.6)',
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <StyledDataGrid
            treeData
            rows={rows}
            columns={columns}
            getTreeDataPath={row => (row as InsightRow).path}
            groupingColDef={groupingColDef}
            getRowHeight={params =>
              (params.model as InsightRow).kind === 'group'
                ? GROUP_ROW_HEIGHT
                : CREATIVE_ROW_HEIGHT
            }
            getRowClassName={params =>
              (params.row as InsightRow).kind === 'group'
                ? 'insights-group-row'
                : 'insights-creative-row'
            }
            disableRowSelectionOnClick
            density="compact"
            sx={{
              border: 0,
              '& .insights-creative-row': { backgroundColor: '#F8FAFC' },
              '& .insights-group-row .MuiDataGrid-cell': { fontWeight: 600 },
            }}
          />
        </Box>
      </Paper>

      <TagEditorDialog
        // A fresh dialog per creative, so one creative's error doesn't show on the next.
        key={openCreative?.creative_key}
        creative={openCreative}
        vocabulary={vocabulary}
        onClose={() => setOpenKey(null)}
        onSetTag={setTag}
      />
    </Box>
  );
}
